import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import ModelViewer from './ModelViewer';
import ExpressionStrip, { stepExpression, exprLabel } from './ExpressionStrip';
import { mergeExpressions } from '../lib/expressions';
import LooksStrip from './LooksStrip';
import { toggleActive } from '../lib/looks';

export default function Lightbox({ item, kind, onClose }) {
  // Mounting the 3D Canvas BEFORE the lightbox slide-in animation finishes
  // makes <Bounds> fit the camera to a half-grown container, then resize
  // again at the end — looking like the model "snaps". We delay Canvas
  // mount until the modal is fully open.
  const [animationDone, setAnimationDone] = useState(false);
  const [morphNames, setMorphNames] = useState([]);
  const [active, setActive] = useState(-1);
  const [osd, setOsd] = useState({ tick: 0, kind: null, text: '' });
  const [activeLooks, setActiveLooks] = useState([]);
  const [flicker, setFlicker] = useState(0);
  const looks = item?.looks;
  const toggleLook = useCallback((t) => {
    setActiveLooks((cur) => {
      const on = !cur.includes(t.id);
      setOsd((o) => ({ tick: o.tick + 1, kind: 'look', text: `${t.name.toUpperCase()} · ${on ? 'ON' : 'OFF'}` }));
      return toggleActive(looks?.toggles, cur, t);
    });
    setFlicker((f) => f + 1);
  }, [looks]);
  const exprList = useMemo(
    () => mergeExpressions(item?.expressions, morphNames).filter((e) => !e.hidden),
    [item?.expressions, morphNames]
  );
  const selectExpr = useCallback((i) => { setActive(i); setOsd((o) => ({ tick: o.tick + 1, kind: 'expr', text: '' })); }, []);

  useEffect(() => { setMorphNames([]); setActive(-1); setActiveLooks([]); setOsd({ tick: 0, kind: null, text: '' }); }, [item?.id]);

  useEffect(() => {
    if (!exprList.length) return undefined;
    const onKey = (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      selectExpr(stepExpression(active, e.key === 'ArrowRight' ? 1 : -1, exprList.length));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [exprList.length, active, selectExpr]);

  useEffect(() => {
    if (!item) { setAnimationDone(false); return; }
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    // .lightbox-in animation is 0.3s; allow a small safety buffer.
    const t = setTimeout(() => setAnimationDone(true), 360);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [item, onClose]);

  if (!item) return null;
  const type = item.type === 'model' || (!item.type && kind === '3d') ? 'model' : item.type || kind;
  const isVideo = type === 'video';
  const is3D = type === 'model';

  // Portal into the inner TV screen so the lightbox stays inside the dark
  // screen area (rather than covering the whole browser). Falls back to
  // document.body during SSR / before mount.
  const target =
    (typeof document !== 'undefined' && document.getElementById('tv-screen-root')) ||
    (typeof document !== 'undefined' ? document.body : null);
  if (!target) return null;

  return createPortal(
    <div className="absolute inset-0 z-[100] flex items-center justify-center p-3 md:p-5 bg-[#0a0502]/94 backdrop-blur-sm"
         onClick={onClose}>
      <div className="relative w-full h-full max-w-[1600px] flex flex-col rounded-lg border-2 border-[#3a2512] bg-[#1a0e05] lightbox-in"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-4 md:p-6 relative flex flex-col flex-1 min-h-0">
          <div className="flex items-start justify-between mb-4 gap-4 shrink-0">
            <div>
              <div className="font-osd text-[#f5a623] text-sm tracking-widest mb-1">// NOW PLAYING</div>
              <h2 className="font-display text-3xl md:text-5xl text-[#ffe9b8] chroma">{item.title}</h2>
              <div className="font-osd text-[#d8b887] text-sm mt-2">
                {formatDate(item.date)}
                {item.runtime && <span className="ml-3">&middot; RUNTIME {item.runtime}</span>}
                {item.medium && <span className="ml-3">&middot; {item.medium}</span>}
                {item.software && <span className="ml-3">&middot; {item.software}</span>}
              </div>
            </div>
            <button onClick={onClose}
                    className="w-10 h-10 border-2 border-[#f5a623] rounded text-[#f5a623] flex items-center justify-center hover:bg-[#f5a623] hover:text-[#1a0e05] transition-colors shrink-0"
                    aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="relative rounded-lg overflow-hidden border-2 border-[#3a2512] bg-[#0a0502] flex-1 min-h-[260px] flex">
            {is3D ? (
              animationDone ? (
                <ModelViewer item={item} expanded fillContainer
                             expression={active >= 0 ? exprList[active]?.key : null} onMorphNames={setMorphNames}
                             activeLooks={activeLooks} />
              ) : (
                <div className="w-full h-full" style={{ background: 'radial-gradient(ellipse at center, #2a1608 0%, #0f0704 75%)' }} />
              )
            ) : isVideo ? (
              <video src={item.src} poster={item.poster} controls autoPlay
                     className="w-full h-full max-h-full object-contain bg-black mx-auto block" />
            ) : (
              <img src={item.src} alt={item.title} className="w-full h-full object-contain mx-auto block" />
            )}
            {!is3D && (
              <div className="absolute inset-0 pointer-events-none" style={{
                background: 'repeating-linear-gradient(to bottom, rgba(0,0,0,0) 0 2px, rgba(0,0,0,0.22) 3px 4px)',
                mixBlendMode: 'multiply'
              }} />
            )}
            {is3D && osd.tick > 0 && osd.kind === 'expr' && exprList.length > 0 && (
              <div key={osd.tick} className="expr-osd absolute top-2 right-2 pointer-events-none font-osd" data-testid="expr-osd">
                {exprLabel(exprList, active).replace(/ \/ \d+/, '')}
              </div>
            )}
            {is3D && osd.kind === 'look' && (
              <div key={osd.tick} className="expr-osd absolute top-2 right-2 pointer-events-none font-osd" data-testid="look-osd">{osd.text}</div>
            )}
            {flicker > 0 && <div key={`f${flicker}`} className="look-flicker absolute inset-0 pointer-events-none" aria-hidden />}
          </div>

          {is3D && looks?.toggles?.length > 0 && (
            <LooksStrip toggles={looks.toggles} active={activeLooks} onToggle={toggleLook} />
          )}

          {is3D && exprList.length > 0 && (
            <ExpressionStrip list={exprList} activeIndex={active} onSelect={selectExpr} showNav={item.exprNav !== false} />
          )}

          <div className="mt-4 grid md:grid-cols-3 gap-5 shrink-0">
            <div className="md:col-span-2">
              <div className="font-osd text-[#f5a623] text-sm tracking-widest mb-2">// SYNOPSIS</div>
              <p className="font-body text-[#d8b887] text-[15px] leading-relaxed">{item.description}</p>
            </div>
            <div className="border-l border-[#3a2512] md:pl-5">
              <div className="font-osd text-[#f5a623] text-sm tracking-widest mb-2">// DETAILS</div>
              <dl className="font-body text-sm text-[#a88552] space-y-2">
                <div><dt className="text-[#8b6a3e] font-osd text-xs">ID</dt><dd>{item.id?.toUpperCase()}</dd></div>
                <div><dt className="text-[#8b6a3e] font-osd text-xs">DATE</dt><dd>{formatDate(item.date)}</dd></div>
                {item.runtime && <div><dt className="text-[#8b6a3e] font-osd text-xs">RUNTIME</dt><dd>{item.runtime}</dd></div>}
                {item.medium && <div><dt className="text-[#8b6a3e] font-osd text-xs">MEDIUM</dt><dd>{item.medium}</dd></div>}
                {item.software && <div><dt className="text-[#8b6a3e] font-osd text-xs">SOFTWARE</dt><dd>{item.software}</dd></div>}
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>,
    target
  );
}

function formatDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }).toUpperCase(); }
  catch { return d; }
}
